// Lib
import React, { useState } from 'react';
import { NativeMarket } from '@capacitor-community/native-market';
import { useDispatch, useSelector } from 'react-redux';

import { refreshPage } from '../../reducers/navigationActions';
import globalLogger from '../../logger';
import { useTranslator } from '../../components/i18n/useTranslator';
import useHideNativeLaunchScreen from '../../capacitor/hooks/useHideNativeLaunchScreen';

// Types
import { ApplicationType } from '../../../common/platform/platformTypes';

// Selectors
import { selectIsMobileAppMode } from '../platformSelector';
import { isCurrentUserStaff } from '../../user/currentUserSelector';

// Components
import FullScreenModal from '../../components/modal/FullScreenModal';
import StyledButton from '../../components/buttons/StyledButton';

// Styles
import './VersionBlockingFullScreenPrompt.scss';
import { autoUpdateInstallUpdate } from '../../../common/electron/electronActions';

// Images
import maintenanceImage from './image-system-down@2x.jpg';
import milanoteLogo from './milanote-logo-vertical@2x.png';

const logger = globalLogger.createChannel('platform.version-blocking');

const performAndroidUpdateAction = async () => {
    try {
        await NativeMarket.openStoreListing({ appId: 'com.milanote.milanoteApp' });
    } catch (error) {
        logger.error('Failed to open store listing', error, { platform: 'android' });
        refreshPage();
    }
};

const performIOSUpdateAction = async () => {
    try {
        await NativeMarket.openStoreListing({ appId: 'id1433852790' });
    } catch (error) {
        logger.error('Failed to open store listing', error, { platform: 'ios' });
        refreshPage();
    }
};

const VersionBlockingFullScreenPrompt: React.FC<{ appType: ApplicationType }> = ({ appType }) => {
    const isMobileAppMode = useSelector(selectIsMobileAppMode);
    const isCurrentUserRoleStaff = useSelector(isCurrentUserStaff);
    const [isOpen, setIsOpen] = useState(true);
    const dispatch = useDispatch();
    const translate = useTranslator('modal', 'platform.versionBlocking');

    // A blocked build never reaches the workspace, so make sure the native launch screen doesn't
    // stay on top of this prompt
    useHideNativeLaunchScreen();

    // allow staff/admin users to close the prompt in case we need to bypass it temporarily
    const handleClose = isCurrentUserRoleStaff ? () => setIsOpen(false) : undefined;

    const performUpdateAction = () => {
        switch (appType) {
            case ApplicationType.androidFlutter:
            case ApplicationType.androidCapacitor:
            case ApplicationType.androidTabletCapacitor:
                return performAndroidUpdateAction();
            case ApplicationType.iphoneSwift:
            case ApplicationType.ipadSwift:
            case ApplicationType.iphoneCapacitor:
            case ApplicationType.ipadCapacitor:
                return performIOSUpdateAction();
            case ApplicationType.electronMac:
                dispatch(autoUpdateInstallUpdate());
                break;
            default:
                refreshPage();
                return;
        }
    };

    if (!isOpen) return null;

    return (
        <FullScreenModal className="version-blocker" fillScreen={isMobileAppMode} close={handleClose}>
            <div className="container">
                <img className="maintenance-image" src={maintenanceImage} />
                <h1 className="title">{translate('title')}</h1>
                <p className="subtitle">
                    {translate('description')}
                </p>
                <StyledButton className="primary" onClick={() => performUpdateAction()}>
                    {translate('reloadButton')}
                </StyledButton>
                <img className="logo" src={milanoteLogo} />
            </div>
        </FullScreenModal>
    );
};

export default VersionBlockingFullScreenPrompt;
